/**
 * Ruta `/solicitudes` (002 · T040; 007 · T050): pestañas Marcaciones (regularizaciones, por
 * defecto) y Dispositivos (re-vinculación).
 *
 * `validateSearch` acepta `tipo` (`marcaciones` | `dispositivos`) y `solicitud` (id de
 * regularización, `reg_<ULID>`). Lo que no encaje se descarta: la pantalla arranca en la pestaña por
 * defecto y sin revisión abierta en vez de romperse con una URL manipulada. Una solicitud bien
 * formada pero inexistente o fuera del alcance abre la revisión y la API responde `404`.
 * Los cambios de pestaña y de solicitud abierta usan `replace` para no ensuciar el historial.
 *
 * 008 · T113: `tipo=justificaciones` (contracts/ui.md §1.1).
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { RegularizationRequestId } from '@fermin/contracts';

import {
  DEFAULT_REQUESTS_TAB,
  RequestsTabsPage,
  type RequestsTab,
} from '../../features/regularizations/requests-tabs-page';

export interface SolicitudesSearch {
  tipo?: RequestsTab;
  solicitud?: string;
}

const TABS: readonly RequestsTab[] = ['marcaciones', 'justificaciones', 'dispositivos'];

export const Route = createFileRoute('/_app/solicitudes')({
  validateSearch: (search: Record<string, unknown>): SolicitudesSearch => {
    const result: SolicitudesSearch = {};
    const tipo = search['tipo'];
    if (typeof tipo === 'string' && (TABS as readonly string[]).includes(tipo)) {
      result.tipo = tipo as RequestsTab;
    }
    const solicitud = RegularizationRequestId.safeParse(search['solicitud']);
    if (solicitud.success) {
      result.solicitud = solicitud.data;
    }
    return result;
  },
  component: function SolicitudesRoute() {
    const { tipo, solicitud } = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });
    return (
      <RequestsTabsPage
        // Una solicitud en la URL siempre abre en Marcaciones (ahí vive su revisión).
        tab={solicitud ? 'marcaciones' : (tipo ?? DEFAULT_REQUESTS_TAB)}
        onTabChange={(next) =>
          void navigate({
            search: (prev) => ({ ...prev, tipo: next, solicitud: undefined }),
            replace: true,
          })
        }
        requestId={solicitud ?? null}
        onRequestChange={(id) =>
          void navigate({
            search: (prev) => ({ ...prev, solicitud: id ?? undefined }),
            replace: true,
          })
        }
      />
    );
  },
});
